import { useMemo } from "react";
import type { CSSProperties } from "react";
import { useTheme } from "@repo/tokens";
import { resolveThemeToken } from "../../utils/theme";
import type {
  ShimmerProps,
  ShimmerShape,
  ShimmerVariant,
  ShimmerStyles,
} from "./types";

const getVariantDefaults = (
  variant: ShimmerVariant,
): {
  width: string | number;
  height: string | number;
  shape: ShimmerShape;
  borderRadius?: number;
} => {
  switch (variant) {
    case "textLine":
      return { width: "100%", height: "20px", shape: "rectangle" };
    case "textBlock":
      return { width: "100%", height: "60px", shape: "rectangle" };
    case "card":
      return {
        width: "100%",
        height: "200px",
        shape: "rounded",
      };
    case "avatar":
      return { width: "48px", height: "48px", shape: "circle" };
    case "image":
      return { width: "100%", height: "auto", shape: "rectangle" };
    default:
      return { width: "100%", height: "20px", shape: "rectangle" };
  }
};

const getShapeStyles = (
  shape: ShimmerShape,
  width: string | number,
  height: string | number,
  borderRadius: number,
  theme: ReturnType<typeof useTheme>["theme"],
): {
  borderRadius: string | number;
  width: string | number;
  height: string | number;
} => {
  const widthNum = typeof width === "string" ? parseFloat(width) : width;
  const heightNum = typeof height === "string" ? parseFloat(height) : height;

  switch (shape) {
    case "circle": {
      const size = Math.min(widthNum || 48, heightNum || 48);
      return {
        borderRadius: `${size / 2}px`,
        width: typeof width === "string" ? width : `${width}px`,
        height: typeof height === "string" ? height : `${height}px`,
      };
    }
    case "rounded":
      return {
        borderRadius: borderRadius || theme.radii.md,
        width: typeof width === "string" ? width : `${width}px`,
        height: typeof height === "string" ? height : `${height}px`,
      };
    case "rectangle":
    default:
      return {
        borderRadius: borderRadius || 0,
        width: typeof width === "string" ? width : `${width}px`,
        height: typeof height === "string" ? height : `${height}px`,
      };
  }
};

export const useShimmerStyles = (
  props: Partial<ShimmerProps>,
): ShimmerStyles => {
  const { theme } = useTheme();

  const {
    variant,
    width: propWidth,
    height: propHeight,
    shape: propShape,
    borderRadius: propBorderRadius = 0,
    baseColor: propBaseColor,
    highlightColor: propHighlightColor,
    animationType = "horizontal",
    animated = true,
    duration = 1500,
    delay = 0,
  } = props;

  const variantDefaults = useMemo(() => getVariantDefaults(variant), [variant]);

  const width = propWidth ?? variantDefaults.width;
  const height = propHeight ?? variantDefaults.height;
  const shape = propShape ?? variantDefaults.shape;
  const borderRadius = propBorderRadius || variantDefaults.borderRadius || 0;

  const baseColor = useMemo(() => {
    if (propBaseColor) {
      const resolved = resolveThemeToken(theme, propBaseColor);
      if (resolved && resolved !== propBaseColor) return resolved;
      return propBaseColor;
    }
    return theme.colors.neutral[90] || "#E6E7E8";
  }, [propBaseColor, theme]);

  const highlightColor = useMemo(() => {
    if (propHighlightColor) {
      const resolved = resolveThemeToken(theme, propHighlightColor);
      if (resolved && resolved !== propHighlightColor) return resolved;
      return propHighlightColor;
    }
    return theme.colors.neutral[100] || "#FFFFFF";
  }, [propHighlightColor, theme]);

  const shapeStyles = useMemo(
    () => getShapeStyles(shape, width, height, borderRadius, theme),
    [shape, width, height, borderRadius, theme],
  );

  const gradient = useMemo(() => {
    return `linear-gradient(90deg, ${baseColor} 0%, ${highlightColor} 50%, ${baseColor} 100%)`;
  }, [baseColor, highlightColor]);

  const container: CSSProperties = {
    ...shapeStyles,
    backgroundColor: baseColor,
    backgroundImage: animated ? gradient : undefined,
    backgroundSize: animated ? "200% 100%" : undefined,
    backgroundRepeat: "no-repeat",
    position: "relative",
    overflow: "hidden",
    ...(animated && {
      animationName: `shimmer-${animationType}`,
      animationDuration: `${duration}ms`,
      animationTimingFunction: "ease-in-out",
      animationIterationCount: "infinite",
      ...(delay > 0 && { animationDelay: `${delay}ms` }),
    }),
  };

  return {
    container,
  };
};
